import React, { useEffect, useMemo, useState } from "react";
import { api, fmt } from "../../../services/erpApi";
import { StatCard, PageHeader, LoadingScreen, Modal, EmptyState } from "../../../components/admin/EssUi";
import { useToast } from "../../../components/admin/ToastContext";
import { Plus, Search, Package, Factory, Banknote, AlertTriangle } from "lucide-react";

const PAGE_SIZE = 15;

const AdminPanelProductsPage = () => {
  const toast = useToast();
  const [data, setData] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [search, setSearch] = useState("");
  const [catFilter, setCatFilter] = useState("all");
  const [page, setPage] = useState(1);

  const [showNew, setShowNew] = useState(false);
  const [creating, setCreating] = useState(false);
  const [categories, setCategories] = useState([]);
  const [uoms, setUoms] = useState([]);
  const [form, setForm] = useState({
    name: "", list_price: "", standard_price: "", default_code: "", barcode: "",
    uom_id: "", categ_id: "", type: "consu", is_active: true,
  });

  useEffect(() => { setPage(1); }, [search, catFilter]);

  const load = async () => {
    try {
      const rows = await api.products.list({ limit: 500 });
      setData(rows || []);
    } catch { setData([]); }
  };

  useEffect(() => {
    (async () => {
      await Promise.all([
        load(),
        api.products.categories().catch(() => []),
        api.products.uoms().catch(() => []),
      ]).then(([, c, u]) => {
        setCategories(c || []);
        setUoms(u || []);
      });
      setLoaded(true);
    })();
  }, []);

  const openNew = async () => {
    setForm({ name: "", list_price: "", standard_price: "", default_code: "", barcode: "", uom_id: "", categ_id: "", type: "consu", is_active: true });
    const [c, u] = await Promise.all([
      api.products.categories().catch(() => []),
      api.products.uoms().catch(() => []),
    ]);
    setCategories(c || []);
    setUoms(u || []);
    setShowNew(true);
  };

  const handleCreate = async () => {
    if (!form.name) { toast("Product name is required", "error"); return; }
    setCreating(true);
    try {
      const body = {
        name: form.name,
        list_price: form.list_price !== "" ? parseFloat(form.list_price) : undefined,
        standard_price: form.standard_price !== "" ? parseFloat(form.standard_price) : undefined,
        default_code: form.default_code || undefined,
        barcode: form.barcode || undefined,
        uom_id: form.uom_id ? parseInt(form.uom_id) : undefined,
        categ_id: form.categ_id ? parseInt(form.categ_id) : undefined,
        type: form.type,
        is_active: form.is_active,
      };
      const created = await api.products.create(body);
      toast(`Product ${created?.name || ""} created`, "success");
      setShowNew(false);
      await load();
    } catch (e) {
      toast(e.message || "Create failed", "error");
    }
    setCreating(false);
  };

  const stats = useMemo(() => {
    const totalQty = data.reduce((s, r) => s + (Number(r.qty_on_hand) || 0), 0);
    const stockValue = data.reduce((s, r) => s + (Number(r.qty_on_hand) || 0) * (Number(r.standard_price) || 0), 0);
    const lowStock = data.filter((r) => (Number(r.qty_available) || 0) < 5).length;
    return { count: data.length, totalQty, stockValue, lowStock };
  }, [data]);

  const filtered = useMemo(() => {
    return data.filter((r) => {
      if (catFilter !== "all" && String(r.categ_id) !== catFilter) return false;
      if (search.trim()) {
        const q = search.toLowerCase();
        const hay = `${r.name || ""} ${r.default_code || ""} ${r.barcode || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [data, search, catFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const inputStyle = {
    width: "100%", padding: "9px 12px", border: "1px solid #cbd5e1", borderRadius: "6px",
    fontSize: "13.5px", outline: "none", background: "#fff", boxSizing: "border-box",
  };
  const labelStyle = { fontSize: "13px", fontWeight: "600", color: "#334155", marginBottom: "6px", display: "block" };
  const FormField = ({ label, children }) => (
    <div style={{ marginBottom: "16px" }}>
      <label style={labelStyle}>{label}</label>
      {children}
    </div>
  );

  if (!loaded) return <LoadingScreen />;

  return (
    <div className="max-w-7xl mx-auto animate-fade-in" style={{ padding: "0 16px" }}>
      <PageHeader
        title="Products"
        subtitle="Product catalog with pricing and stock quantities"
        actions={
          <button onClick={openNew} className="ess-btn ess-btn-primary">
            <Plus size={16} />
            New Product
          </button>
        }
      />

      <div className="ess-sg ess-sg4" style={{ marginBottom: "24px" }}>
        <StatCard label="Products" value={String(stats.count)} icon={Package} trend="Catalog items" />
        <StatCard label="Units on Hand" value={fmt.qty(stats.totalQty)} icon={Factory} trend="Total quantity" />
        <StatCard label="Stock Value" value={fmt.moneyShort(stats.stockValue)} icon={Banknote} trend={fmt.money(stats.stockValue)} />
        <StatCard label="Low Stock" value={String(stats.lowStock)} icon={AlertTriangle} trend="Available &lt; 5 units" />
      </div>

      <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", marginBottom: "24px", background: "white", padding: "16px", borderRadius: "8px", border: "1px solid #e2e7e2" }}>
        <div style={{ flex: 1, minWidth: "200px", position: "relative" }}>
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "#94a3b8" }} />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, code, barcode..." style={{ ...inputStyle, padding: "9px 12px 9px 36px" }} />
        </div>
        <select value={catFilter} onChange={(e) => setCatFilter(e.target.value)} style={{ ...inputStyle, minWidth: "180px", cursor: "pointer" }}>
          <option value="all">All Categories</option>
          {categories.map((c) => <option key={c.id} value={c.id}>{c.name || c.complete_name}</option>)}
        </select>
      </div>

      <div style={{ background: "white", borderRadius: "8px", border: "1px solid #e2e7e2", overflow: "hidden" }}>
        {paginated.length === 0 ? (
          <EmptyState icon="📦" title="No products" subtitle="Create your first product with the New Product button" />
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
              <thead>
                <tr style={{ background: "#fafbfa", borderBottom: "1px solid #e2e7e2" }}>
                  <th className="ess-th" style={{ minWidth: "180px" }}>Product</th>
                  <th className="ess-th" style={{ minWidth: "100px" }}>Code</th>
                  <th className="ess-th" style={{ minWidth: "130px" }}>Category</th>
                  <th className="ess-th ess-th-r" style={{ minWidth: "110px" }}>Cost</th>
                  <th className="ess-th ess-th-r" style={{ minWidth: "110px" }}>Sale Price</th>
                  <th className="ess-th" style={{ textAlign: "center", minWidth: "90px" }}>On Hand</th>
                  <th className="ess-th" style={{ textAlign: "center", minWidth: "90px" }}>Available</th>
                </tr>
              </thead>
              <tbody>
                {paginated.map((row, index) => (
                  <tr key={row.id} className="ess-tr" style={{ borderBottom: index < paginated.length - 1 ? "1px solid #f1f3f0" : "none" }}>
                    <td className="ess-td" style={{ fontWeight: "500", color: "#1e293b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{row.name || "—"}</td>
                    <td className="ess-td" style={{ color: "#64748b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{row.default_code || "—"}</td>
                    <td className="ess-td" style={{ color: "#475569", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{row.category_name || "—"}</td>
                    <td className="ess-td" style={{ color: "#475569", textAlign: "right", whiteSpace: "nowrap" }}>{fmt.money(row.standard_price)}</td>
                    <td className="ess-td" style={{ fontWeight: "600", color: "#225f2b", textAlign: "right", whiteSpace: "nowrap" }}>{fmt.money(row.list_price)}</td>
                    <td className="ess-td" style={{ fontWeight: "600", color: "#1e293b", textAlign: "center", whiteSpace: "nowrap" }}>{fmt.qty(row.qty_on_hand)}</td>
                    <td className="ess-td" style={{ textAlign: "center", whiteSpace: "nowrap" }}>
                      <span style={{ background: (Number(row.qty_available) || 0) <= 0 ? "#fff1f2" : (Number(row.qty_available) || 0) < 5 ? "#fffbeb" : "#f0f7f0", color: (Number(row.qty_available) || 0) <= 0 ? "#be123c" : (Number(row.qty_available) || 0) < 5 ? "#b45309" : "#225f2b", padding: "3px 10px", borderRadius: "6px", fontSize: "12px", fontWeight: "500", whiteSpace: "nowrap" }}>
                        {fmt.qty(row.qty_available)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 16px", borderTop: "1px solid #e2e7e2", background: "#fafbfa" }}>
          <span style={{ fontSize: "13px", color: "#64748b" }}>
            Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length}
          </span>
          <div style={{ display: "flex", gap: "4px" }}>
            <button disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} style={{ padding: "6px 12px", border: "1px solid #cbd5e1", borderRadius: "6px", background: page <= 1 ? "#f1f5f9" : "white", color: page <= 1 ? "#94a3b8" : "#334155", fontSize: "13px", fontWeight: "500", cursor: page <= 1 ? "not-allowed" : "pointer" }}>← Prev</button>
            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
              const start = Math.max(1, Math.min(page - 2, totalPages - 4));
              const p = start + i;
              if (p > totalPages) return null;
              return <button key={p} onClick={() => setPage(p)} style={{ padding: "6px 12px", border: "1px solid #cbd5e1", borderRadius: "6px", background: p === page ? "#225f2b" : "white", color: p === page ? "white" : "#334155", fontSize: "13px", fontWeight: p === page ? "600" : "500", cursor: "pointer" }}>{p}</button>;
            })}
            <button disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} style={{ padding: "6px 12px", border: "1px solid #cbd5e1", borderRadius: "6px", background: page >= totalPages ? "#f1f5f9" : "white", color: page >= totalPages ? "#94a3b8" : "#334155", fontSize: "13px", fontWeight: "500", cursor: page >= totalPages ? "not-allowed" : "pointer" }}>Next →</button>
          </div>
        </div>
      </div>

      <Modal open={showNew} onClose={() => setShowNew(false)} title="New Product" size="md">
        <div>
          <FormField label="Product Name *">
            <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="e.g. HP Laptop 14" style={inputStyle} />
          </FormField>
          <div style={{ display: "grid", gap: "16px" }} className="ess-rg-2">
            <FormField label="Category">
              <select value={form.categ_id} onChange={(e) => setForm((f) => ({ ...f, categ_id: e.target.value }))} style={inputStyle}>
                <option value="">Select category…</option>
                {categories.map((c) => <option key={c.id} value={c.id}>{c.name || c.complete_name}</option>)}
              </select>
            </FormField>
            <FormField label="Unit of Measure">
              <select value={form.uom_id} onChange={(e) => setForm((f) => ({ ...f, uom_id: e.target.value }))} style={inputStyle}>
                <option value="">Select UoM…</option>
                {uoms.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            </FormField>
            <FormField label="Sale Price (FCFA)">
              <input type="number" min="0" value={form.list_price} onChange={(e) => setForm((f) => ({ ...f, list_price: e.target.value }))} placeholder="0" style={inputStyle} />
            </FormField>
            <FormField label="Cost (FCFA)">
              <input type="number" min="0" value={form.standard_price} onChange={(e) => setForm((f) => ({ ...f, standard_price: e.target.value }))} placeholder="0" style={inputStyle} />
            </FormField>
            <FormField label="Internal Reference">
              <input value={form.default_code} onChange={(e) => setForm((f) => ({ ...f, default_code: e.target.value }))} placeholder="PROD-001" style={inputStyle} />
            </FormField>
            <FormField label="Barcode">
              <input value={form.barcode} onChange={(e) => setForm((f) => ({ ...f, barcode: e.target.value }))} placeholder="123456789" style={inputStyle} />
            </FormField>
          </div>
          <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end", borderTop: "1px solid #e2e7e2", paddingTop: "20px" }}>
            <button onClick={() => setShowNew(false)} className="ess-btn ess-btn-secondary">Cancel</button>
            <button onClick={handleCreate} disabled={creating} className="ess-btn ess-btn-primary">
              {creating ? "Creating…" : "Create Product"}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default AdminPanelProductsPage;
